import {
  Box,
  Button,
  HStack,
  Popover,
  PopoverBody,
  PopoverContent,
  PopoverTrigger,
  Portal,
  useDisclosure,
} from "@chakra-ui/react";
import clsx from "clsx";
import React from "react";
import { useTranslation } from "react-i18next";
import {
  CalendarDateRange,
  CalendarView,
} from "shared/components/input/date/CalendarView";
import { formatDateDisplay } from "shared/components/input/date/DateInputUtils";
import { PopoverOverlay } from "shared/components/popover/PopoverOverlay";
import { ArrowDropDownIcon } from "shared/icons/ArrowDropDownIcon";
import { InputCalendarIcon } from "shared/icons/InputCalendarIcon";

export interface DateRangeButtonProps {
  value: CalendarDateRange;
  onChangeValue: (range: CalendarDateRange) => void;
}

export const DateRangeButton: React.FC<DateRangeButtonProps> = ({
  value,
  onChangeValue,
}) => {
  const { t } = useTranslation(["common"]);

  const { isOpen, onOpen, onClose } = useDisclosure();

  return (
    <Popover
      placement="bottom-start"
      offset={[0, 0]}
      isOpen={isOpen}
      onOpen={onOpen}
      onClose={onClose}
    >
      <PopoverTrigger>
        <Button
          as={Box}
          paddingStart="12px"
          paddingEnd="2px"
          paddingY="2px"
          shadow="xs"
          borderRadius="6px"
          colorScheme="secondary"
          size="xs"
          cursor="pointer"
          leftIcon={<InputCalendarIcon w="20px" h="20px" />}
          rightIcon={
            <ArrowDropDownIcon
              transform={isOpen ? "rotate(-180deg)" : "rotate(0deg)"}
              transitionProperty="transform"
              transitionDuration="slow"
            />
          }
          className={clsx({
            "mb-daterange__is-open": isOpen,
            "mb-daterange__has-value": value.start || value.end,
          })}
          sx={{
            "&.mb-daterange__has-value:not(.mb-daterange__is-open)": {
              borderColor: "primary.300",
              background: "primary.50",
              color: "primary.800",
            },
            "&.mb-daterange__is-open": {
              borderColor: "primary.500",
            },
          }}
        >
          {!value.start && !value.end
            ? t("common:all")
            : [
                value.start ? formatDateDisplay(value.start) : undefined,
                value.end ? formatDateDisplay(value.end) : undefined,
              ]
                .filter(Boolean)
                .join(" - ")}
        </Button>
      </PopoverTrigger>
      <PopoverOverlay />
      {/* Using Portal avoids messing up the input tab order */}
      <Portal>
        <PopoverContent w="unset" border="none">
          <PopoverBody p={0}>
            <CalendarView
              months={2}
              value={value}
              onSelectDateRange={onChangeValue}
            />
            <HStack borderTopWidth="1px" padding="16px">
              <Button
                variant="subtle"
                size="sm"
                onClick={() => {
                  onChangeValue({ start: undefined, end: undefined });
                }}
              >
                {t("common:clear")}
              </Button>
            </HStack>
          </PopoverBody>
        </PopoverContent>
      </Portal>
    </Popover>
  );
};
